import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm, Space, Row, Col } from 'antd';
import { DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { CollapseBlock, QuestionModal } from '../../../../../common/index.jsx';
import Form from '../../../../../antd/form/Form/Form.jsx';
import FormTextArea from '../../../../../antd/form/FormTextArea/FormTextArea.jsx';
import FormButtonsContainer from '../../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormButton from '../../../../../antd/form/FormButton/FormButton.jsx';
import FormSwitch from '../../../../../antd/form/FormSwitch/FormSwitch.jsx';
import FormSelect from '../../../../../antd/form/FormSelect/FormSelect.jsx';
import { empty } from '../../../../../antd/form/FormValidation.js';
import { IconButton } from '../../../../../common/controls/Controls.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import PropTypes from 'prop-types';

const QuestionForm = ({
  initials,
  onSubmit,
  answersLocked = false,
  quizOptions = [],
  quizValue,
}) => {
  const { t } = useTranslation();
  const { errorMessage } = useToastMessages();

  // Antd form instance to manipulate answers
  const [form] = AntdForm.useForm();
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null);
  const removeRef = useRef(null);
  const answersWatch = AntdForm.useWatch('answers', form);

  // Jednoduchý fail handler – zachytí len chýbajúce odpoveď alebo žiadnu správnu
  const handleFinishFailed = (errorInfo) => {
    const noAnswersMsg = t('Pridajte aspoň jednu odpoveď');
    const noCorrectMsg = t('Označte aspoň jednu odpoveď ako správnu');
    if (Array.isArray(errorInfo?.errorFields)) {
      const field = errorInfo.errorFields.find(
        (f) => Array.isArray(f.name) && f.name[0] === 'answers',
      );
      if (field && Array.isArray(field.errors)) {
        if (field.errors.includes(noAnswersMsg)) {
          errorMessage({
            message: t('Kvízová otázka musí mať aspoň jednu odpoveď'),
            key: 'answers-missing',
          });
        } else if (field.errors.includes(noCorrectMsg)) {
          errorMessage({
            message: t('Označte aspoň jednu odpoveď ako správnu'),
            key: 'answers-one-correct',
          });
        }
      }
    }
  };

  // Submit – žiadna špeciálna sanitizácia (viac správnych je povolených)
  const handleFinish = (values) => {
    onSubmit && onSubmit(values);
  };

  return (
    <Form
      form={form}
      initialValues={initials}
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
    >
      {/* Sekcia otázky */}
      <CollapseBlock
        title={t('Kvízová otázka')}
        style={{ marginBottom: 10 }}
        collapsible={true}
      >
        <FormSelect
          label={t('Kvíz')}
          options={quizOptions}
          disabled={true}
          value={quizValue}
        />

        <FormTextArea
          label={t('Text kvízovej otázky')}
          name={'text'}
          rules={[empty(t)]}
        />
      </CollapseBlock>

      {/* Odpovede */}
      <CollapseBlock
        title={t('Odpovede')}
        style={{ marginBottom: 10 }}
        collapsible={true}
      >
        {answersLocked && (
          <div
            style={{
              background: '#fffbe6',
              border: '1px solid #ffe58f',
              padding: '8px 12px',
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {t(
              'Odpovede nemožno upravovať, pretože na otázku už boli podané študentské odpovede.',
            )}
          </div>
        )}
        <AntdForm.List
          name="answers"
          rules={
            answersLocked
              ? []
              : [
                  {
                    validator: async (_, answers) => {
                      if (!answers || answers.length === 0) {
                        return Promise.reject(
                          new Error(t('Pridajte aspoň jednu odpoveď')),
                        );
                      }
                      const hasCorrect = answers.some((a) => a && a.is_correct);
                      if (!hasCorrect) {
                        return Promise.reject(
                          new Error(
                            t('Označte aspoň jednu odpoveď ako správnu'),
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]
          }
        >
          {(fields, { add, remove }) => {
            removeRef.current = answersLocked ? () => {} : remove;
            return (
              <div>
                {fields.map(({ key, name }, index) => (
                  <div
                    key={key}
                    style={{
                      marginBottom: 6,
                      position: 'relative',
                      background: answersWatch?.[name]?.is_correct
                        ? '#f6ffed'
                        : '#fff',
                      border: answersWatch?.[name]?.is_correct
                        ? '1px solid #b7eb8f'
                        : '1px solid #f0f0f0',
                      borderRadius: 4,
                      padding: '6px 8px',
                      opacity: answersLocked ? 0.85 : 1,
                    }}
                  >
                    {answersWatch?.[name]?.is_correct && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          background: '#52c41a',
                          color: '#fff',
                          padding: '2px 6px',
                          fontSize: 12,
                          fontWeight: 600,
                          borderBottomLeftRadius: 4,
                        }}
                      >
                        {t('Správna')}
                      </div>
                    )}
                    <Row
                      align="middle"
                      justify="end"
                      style={{ marginBottom: 2 }}
                    >
                      <Col flex="auto">
                        <FormTextArea
                          label={`${t('Odpoveď')} ${index + 1}`}
                          name={[name, 'text']}
                          rules={[empty(t)]}
                          placeholder={t('Text odpovede')}
                          rows={2}
                          itemProps={{ style: { marginBottom: 6 } }}
                          disabled={answersLocked}
                        />
                      </Col>
                      <Col align="middle">
                        {!answersLocked && (
                          <IconButton
                            onClick={() => setPendingRemoveIndex(name)}
                            icon={<DeleteFilled />}
                            tooltip={t('Odstrániť odpoveď')}
                          />
                        )}
                      </Col>
                    </Row>
                    <Row style={{ marginBottom: 4 }}>
                      <Col>
                        <FormSwitch
                          name={[name, 'is_correct']}
                          label={t('Správna')}
                          disabled={answersLocked}
                        />
                      </Col>
                    </Row>
                  </div>
                ))}
                {!answersLocked && (
                  <Space style={{ marginTop: 4 }}>
                    <IconButton
                      icon={<PlusOutlined />}
                      tooltip={t('Pridať odpoveď')}
                      onClick={() => add({ is_correct: false })}
                    />
                  </Space>
                )}
              </div>
            );
          }}
        </AntdForm.List>
      </CollapseBlock>

      <FormButtonsContainer>
        <FormButton submitOnClick={true} submitKey={'quizQuestion'}>
          {t('Uložiť')}
        </FormButton>
      </FormButtonsContainer>

      <QuestionModal
        visible={!answersLocked && pendingRemoveIndex !== null}
        headerTitle={t('Odstrániť odpoveď')}
        question={t('Naozaj chcete vymazať túto odpoveď?')}
        yes={t('Áno')}
        chosenPositive={() => {
          if (
            !answersLocked &&
            removeRef.current &&
            pendingRemoveIndex !== null
          ) {
            removeRef.current(pendingRemoveIndex);
          }
          setPendingRemoveIndex(null);
        }}
        toggle={() => setPendingRemoveIndex(null)}
      />
    </Form>
  );
};

QuestionForm.propTypes = {
  initials: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  answersLocked: PropTypes.bool,
  quizOptions: PropTypes.array,
  quizValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default QuestionForm;
